import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import type { CohortFatigueSummary } from "../data/types";
import { RiskTierBadge } from "./RiskTierBadge";
import { Sparkline, TrendChip, tierColor } from "./primitives";
import type { TrendDirection } from "../data/types";

interface CohortRiskTableProps {
  rows: CohortFatigueSummary[];
  onSelectCohort?: (cohortId: string) => void;
  /** Hide site/shift columns on tighter layouts. */
  compact?: boolean;
}

const trendLabel = (t: TrendDirection): string =>
  t === "up" ? "Rising" : t === "down" ? "Falling" : "Stable";

/** Sortable-friendly cohort comparison table: site, shift, FED, LRM, CDI, PDC. */
export const CohortRiskTable: React.FC<CohortRiskTableProps> = ({
  rows,
  onSelectCohort,
  compact = false,
}) => {
  const theme = useTheme();

  return (
    <TableContainer>
      <Table size="small" aria-label="Cohort fatigue risk comparison">
        <TableHead>
          <TableRow sx={{ "& th": { fontWeight: 700, fontSize: 12, color: "text.secondary" } }}>
            <TableCell>Cohort</TableCell>
            {!compact && <TableCell>Site</TableCell>}
            {!compact && <TableCell>Shift</TableCell>}
            <TableCell align="right">FED / 1k</TableCell>
            <TableCell align="right">LRM %</TableCell>
            <TableCell align="right">CDI</TableCell>
            <TableCell align="right">PDC %</TableCell>
            <TableCell>Trend</TableCell>
            <TableCell>Tier</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const interactive = Boolean(onSelectCohort);
            return (
              <TableRow
                key={row.cohort.id}
                hover={interactive}
                tabIndex={interactive ? 0 : undefined}
                aria-label={interactive ? `${row.cohort.name}, open detail` : undefined}
                onClick={() => onSelectCohort?.(row.cohort.id)}
                onKeyDown={(e) => {
                  if (interactive && (e.key === "Enter" || e.key === " ")) {
                    e.preventDefault();
                    onSelectCohort?.(row.cohort.id);
                  }
                }}
                sx={{ cursor: interactive ? "pointer" : "default" }}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {row.cohort.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {row.cohort.sector} · {row.cohort.headcount} people
                  </Typography>
                </TableCell>
                {!compact && <TableCell>{row.cohort.site}</TableCell>}
                {!compact && (
                  <TableCell sx={{ textTransform: "capitalize" }}>
                    {row.cohort.shiftType}
                  </TableCell>
                )}
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {row.fedPer1000}
                </TableCell>
                <TableCell align="right">{row.lrmPct}%</TableCell>
                <TableCell align="right">{row.cdiScore}</TableCell>
                <TableCell align="right">{row.pdcPct}%</TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Sparkline
                      values={row.sparkline}
                      color={tierColor(row.riskTier, theme)}
                      width={64}
                      height={22}
                    />
                    <TrendChip
                      trend={row.trend}
                      label={trendLabel(row.trend)}
                      goodDirection="down"
                    />
                  </Box>
                </TableCell>
                <TableCell>
                  <RiskTierBadge tier={row.riskTier} size="small" />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
